/**
 * Lista de conteos de inventario de la sucursal del usuario.
 *
 * - Conteos abiertos: cualquier persona con acceso se une con su dispositivo
 *   y su pistola (pantalla de escaneo multi-usuario).
 * - Nuevo conteo: alcance toda la sucursal, una sola marca o un modelo de
 *   una marca. El servidor valida que el alcance no se solape con otro
 *   conteo abierto.
 * - Historial: conteos cerrados (los del módulo nuevo con detalle por
 *   usuario; los de la pantalla vieja se muestran tal cual quedaron).
 */
import React, { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  MenuItem,
  TextField,
  LinearProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { useGlobalMDAlert } from "context";
import { useMaterialUIController } from "context";
import { FP, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { alcanceLabel, puedeConteos } from "modules/conteos/comunes";

const chipTotalesSx = (bg, color) => ({
  height: 22,
  fontWeight: 700,
  fontSize: "0.66rem",
  bgcolor: bg,
  color,
});

/** Chips Esp/Cont/Pend/Sobr que comparten abiertos e historial. */
function ChipsTotales({ conteo }) {
  return (
    <Box sx={{ display: "flex", gap: 0.6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
      <Chip label={`Esp ${conteo.totalesperado ?? 0}`} size="small" sx={chipTotalesSx(FP.color.surfaceAlt, undefined)} />
      <Chip label={`Cont ${conteo.totalcontado ?? 0}`} size="small" sx={chipTotalesSx(FP.color.successSoft, FP.color.successDark)} />
      <Chip label={`Pend ${conteo.totalpendiente ?? 0}`} size="small" sx={chipTotalesSx(FP.color.warningSoft, "#92400E")} />
      <Chip label={`Sobr ${conteo.totalsobrante ?? 0}`} size="small" sx={chipTotalesSx(FP.color.dangerSoft, FP.color.danger)} />
    </Box>
  );
}

/** Lista de chasis como chips (detalle del historial). */
function ChipsChasis({ titulo, items, color }) {
  return (
    <Box sx={{ mt: 1.25 }}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.72rem",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color,
        }}
      >
        {titulo} ({items.length})
      </Typography>
      {items.length > 0 ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.6 }}>
          {items.map((it, i) => (
            <Chip
              key={`${titulo}-${it.chasis || it}-${i}`}
              label={it.usuario ? `${it.chasis} · ${it.usuario}` : it.chasis || it}
              size="small"
              sx={{ height: 22, fontSize: "0.68rem", fontFamily: "monospace", bgcolor: FP.color.surfaceAlt }}
            />
          ))}
        </Box>
      ) : (
        <Typography sx={{ color: FP.color.textMuted, fontSize: "0.76rem", mt: 0.3 }}>—</Typography>
      )}
    </Box>
  );
}

export default function Conteos() {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;
  const puedeDescartar = puedeConteos(roleDetalles, "borrar");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [sucursalName, setSucursalName] = useState("");
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]); // [{marca, modelo, unidades}]
  const [abiertos, setAbiertos] = useState([]);

  const [historial, setHistorial] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [historialSel, setHistorialSel] = useState(null); // `${origen}-${id}`
  const [detalle, setDetalle] = useState(null); // detalle cargado (tabla nueva)
  const [detalleLoading, setDetalleLoading] = useState(false);

  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [alcance, setAlcance] = useState("T");
  const [marca, setMarca] = useState("");
  const [modelo, setModelo] = useState("");
  const [abriendo, setAbriendo] = useState(false);

  const [descartar, setDescartar] = useState(null); // conteo abierto a descartar
  const [descartando, setDescartando] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    setLoadError("");
    post("/api/conteos_config")
      .then(({ ok, data }) => {
        if (!ok) {
          setLoadError(data?.Error || "No se pudieron cargar los conteos.");
          return;
        }
        const codigo = String(data?.sucursal ?? "").trim();
        const descripcion = String(data?.descripcion ?? "").trim();
        setSucursalName(descripcion ? `${codigo}-${descripcion}` : codigo || "—");
        setMarcas(data.marcas || []);
        setModelos(data.modelos || []);
        setAbiertos(data.abiertos || []);
      })
      .catch((err) => setLoadError("Error cargando los conteos: " + (err?.message || "desconocido")))
      .finally(() => setLoading(false));
  }, [post]);

  const cargarHistorial = useCallback(() => {
    setHistorialLoading(true);
    post("/api/conteos_historial")
      .then(({ ok, data }) => setHistorial((ok && data && data.conteos) || []))
      .catch(() => setHistorial([]))
      .finally(() => setHistorialLoading(false));
  }, [post]);

  useEffect(() => {
    cargar();
    cargarHistorial();
  }, [cargar, cargarHistorial]);

  function abrirConteo() {
    if (alcance !== "T" && !marca) {
      showAlert({ message: "Selecciona la marca del conteo", color: "warning", autoHideDuration: 3000 });
      return;
    }
    if (alcance === "D" && !modelo) {
      showAlert({ message: "Selecciona el modelo del conteo", color: "warning", autoHideDuration: 3000 });
      return;
    }
    setAbriendo(true);
    post("/api/conteos_abrir", {
      alcance,
      marca: alcance !== "T" ? marca : "",
      modelo: alcance === "D" ? modelo : "",
    })
      .then(({ ok, data }) => {
        setAbriendo(false);
        if (!ok) {
          showAlert({
            message: data?.Error || "No se pudo abrir el conteo",
            color: "error",
            autoHideDuration: 4500,
          });
          return;
        }
        setNuevoOpen(false);
        navigate(`/inventario/conteos/activo?id=${data.id}`);
      })
      .catch((err) => {
        setAbriendo(false);
        showAlert({ message: "Error de red: " + err.message, color: "error", autoHideDuration: 4000 });
      });
  }

  function descartarConteo() {
    if (!descartar) return;
    setDescartando(true);
    post("/api/conteos_descartar", { conteo_id: descartar.id })
      .then(({ ok, data }) => {
        setDescartando(false);
        setDescartar(null);
        if (!ok) {
          showAlert({
            message: data?.Error || "No se pudo descartar el conteo",
            color: "error",
            autoHideDuration: 4000,
          });
          return;
        }
        showAlert({ message: "Conteo descartado", color: "info", autoHideDuration: 3000 });
        cargar();
      })
      .catch((err) => {
        setDescartando(false);
        setDescartar(null);
        showAlert({ message: "Error de red: " + err.message, color: "error", autoHideDuration: 4000 });
      });
  }

  function toggleHistorial(h) {
    const clave = `${h.origen}-${h.id}`;
    if (historialSel === clave) {
      setHistorialSel(null);
      setDetalle(null);
      return;
    }
    setHistorialSel(clave);
    setDetalle(null);
    if (h.origen === "conteos") {
      setDetalleLoading(true);
      post("/api/conteos_detalle", { conteo_id: h.id })
        .then(({ ok, data }) => setDetalle(ok ? data : null))
        .catch(() => setDetalle(null))
        .finally(() => setDetalleLoading(false));
    }
  }

  const seccionTitulo = (texto, color = FP.color.textSecondary) => (
    <Typography
      sx={{
        mb: 1.2,
        color,
        fontWeight: 700,
        fontSize: "0.68rem",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
      }}
    >
      {texto}
    </Typography>
  );

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      {/* Header */}
      <Box
        sx={{
          pl: { xs: 1.5, md: 3 },
          pr: { xs: 2, md: 3 },
          py: { xs: 1.75, md: 2 },
          bgcolor: FP.color.cardBg,
          borderBottom: `1px solid ${FP.color.border}`,
          position: "sticky",
          top: 0,
          zIndex: 20,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Box display="flex" alignItems="center" gap={1.2} minWidth={0}>
            <IconButton
              size="small"
              onClick={() => navigate("/inventario/informemotores")}
              sx={{
                color: FP.color.textSecondary,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 1.4,
                width: 36,
                height: 36,
                "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Box minWidth={0}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.15rem", md: "1.25rem" },
                  color: FP.color.textPrimary,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.2,
                }}
              >
                Conteos de Inventario
              </Typography>
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: FP.color.textSecondary,
                }}
              >
                {sucursalName}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<AssessmentOutlinedIcon />}
              onClick={() => navigate("/inventario/conteos/reporte")}
              sx={{
                color: FP.color.navy,
                borderColor: FP.color.navy,
                borderRadius: 1.6,
                textTransform: "none",
                fontWeight: 800,
                "&:hover": { borderColor: FP.color.navy, bgcolor: FP.color.surfaceAlt },
              }}
            >
              Reportes
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setAlcance("T");
                setMarca("");
                setModelo("");
                setNuevoOpen(true);
              }}
              sx={{
                flexShrink: 0,
                bgcolor: FP.color.navy,
                color: "#FFFFFF",
                borderRadius: 1.6,
                textTransform: "none",
                fontWeight: 800,
                boxShadow: "none",
                "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              }}
            >
              Nuevo conteo
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 960, mx: "auto" }}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={10} gap={2}>
            <CircularProgress sx={{ color: FP.color.navy }} />
            <Typography sx={{ color: FP.color.textSecondary }}>Cargando conteos…</Typography>
          </Box>
        ) : loadError ? (
          <Card
            elevation={0}
            sx={{ borderRadius: 2.5, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.cardBg }}
          >
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1.5}>
                <ReportProblemRoundedIcon sx={{ fontSize: 48, color: FP.color.danger }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700, textAlign: "center" }}>
                  {loadError}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Conteos abiertos */}
            {seccionTitulo(`Conteos abiertos (${abiertos.length})`)}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mb: 3.5 }}>
              {abiertos.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: "center",
                    borderRadius: 2,
                    border: `1px dashed ${FP.color.border}`,
                    bgcolor: FP.color.cardBg,
                  }}
                >
                  <Typography sx={{ color: FP.color.textSecondary }}>
                    No hay conteos abiertos. Inicia uno con &quot;Nuevo conteo&quot;.
                  </Typography>
                </Box>
              ) : (
                abiertos.map((c) => {
                  const avance = c.totalesperado
                    ? Math.round(((c.totalcontado || 0) * 100) / c.totalesperado)
                    : 0;
                  return (
                    <Box
                      key={c.id}
                      sx={{
                        px: { xs: 1.75, md: 2 },
                        py: 1.75,
                        borderRadius: 2,
                        border: `1px solid ${FP.color.borderStrong}`,
                        bgcolor: FP.color.cardBg,
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} flexWrap="wrap">
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography sx={{ fontWeight: 800, fontSize: "0.98rem", color: FP.color.textPrimary }}>
                              Conteo #{c.id}
                            </Typography>
                            <Chip
                              label={alcanceLabel(c)}
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: "0.68rem",
                                bgcolor: c.alcance !== "T" ? FP.color.warningSoft : FP.color.surfaceAlt,
                                color: c.alcance !== "T" ? "#92400E" : FP.color.textSecondary,
                              }}
                            />
                          </Box>
                          <Typography sx={{ mt: 0.35, color: FP.color.textSecondary, fontSize: "0.78rem" }}>
                            Abierto por {c.abierto_por || "—"}
                            {c.abierto_en ? ` · ${c.abierto_en}` : ""}
                          </Typography>
                        </Box>
                        <ChipsTotales conteo={c} />
                      </Box>
                      <Box sx={{ mt: 1.25, display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, avance)}
                            sx={{
                              height: 8,
                              borderRadius: 999,
                              bgcolor: FP.color.surfaceAlt,
                              "& .MuiLinearProgress-bar": {
                                bgcolor: FP.color.successDark,
                                borderRadius: 999,
                              },
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            color: FP.color.textSecondary,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {avance}%
                        </Typography>
                        {puedeDescartar ? (
                          <IconButton
                            size="small"
                            title="Descartar conteo"
                            onClick={() => setDescartar(c)}
                            sx={{ color: FP.color.danger }}
                          >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        ) : null}
                        <Button
                          variant="contained"
                          startIcon={<QrCodeScannerRoundedIcon />}
                          onClick={() => navigate(`/inventario/conteos/activo?id=${c.id}`)}
                          sx={{
                            bgcolor: FP.color.navy,
                            color: "#FFFFFF",
                            borderRadius: 1.6,
                            textTransform: "none",
                            fontWeight: 800,
                            boxShadow: "none",
                            "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                          }}
                        >
                          Unirse
                        </Button>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Historial */}
            {seccionTitulo("Historial de conteos")}
            {historialLoading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress size={28} sx={{ color: FP.color.navy }} />
              </Box>
            ) : historial.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" py={5} gap={1}>
                <HistoryRoundedIcon sx={{ fontSize: 40, color: FP.color.textMuted }} />
                <Typography sx={{ color: FP.color.textSecondary }}>
                  Aún no hay conteos cerrados para esta sucursal.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {historial.map((h) => {
                  const clave = `${h.origen}-${h.id}`;
                  const abierto = historialSel === clave;
                  return (
                    <Box
                      key={clave}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        bgcolor: FP.color.cardBg,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        onClick={() => toggleHistorial(h)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          p: 1.5,
                          cursor: "pointer",
                          "&:hover": { bgcolor: FP.color.surfaceAlt },
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "0.9rem" }}>
                              {h.fecha || "—"}
                            </Typography>
                            <Chip
                              label={alcanceLabel(h)}
                              size="small"
                              sx={{
                                height: 20,
                                fontWeight: 700,
                                fontSize: "0.64rem",
                                bgcolor: h.alcance !== "T" ? FP.color.warningSoft : FP.color.surfaceAlt,
                                color: h.alcance !== "T" ? "#92400E" : FP.color.textSecondary,
                              }}
                            />
                          </Box>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.76rem" }}>
                            {h.usuario ? `Por ${h.usuario}` : ""}
                          </Typography>
                        </Box>
                        <ChipsTotales conteo={h} />
                      </Box>
                      {abierto ? (
                        <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: `1px solid ${FP.color.border}` }}>
                          {h.origen === "legacy" ? (
                            <>
                              <ChipsChasis
                                titulo="Contados"
                                items={(h.contados || []).map((ch) => ({ chasis: ch }))}
                                color={FP.color.successDark}
                              />
                              <ChipsChasis
                                titulo="Pendientes"
                                items={(h.pendientes || []).map((ch) => ({ chasis: ch }))}
                                color="#92400E"
                              />
                              <ChipsChasis
                                titulo="Sobrantes"
                                items={(h.sobrantes || []).map((ch) => ({ chasis: ch }))}
                                color={FP.color.danger}
                              />
                            </>
                          ) : detalleLoading ? (
                            <Box display="flex" justifyContent="center" py={3}>
                              <CircularProgress size={22} sx={{ color: FP.color.navy }} />
                            </Box>
                          ) : detalle ? (
                            <>
                              <ChipsChasis titulo="Contados" items={detalle.contados || []} color={FP.color.successDark} />
                              <ChipsChasis titulo="Faltantes" items={detalle.pendientes || []} color="#92400E" />
                              <ChipsChasis titulo="Sobrantes" items={detalle.sobrantes || []} color={FP.color.danger} />
                            </>
                          ) : (
                            <Typography sx={{ color: FP.color.textMuted, fontSize: "0.8rem", py: 2 }}>
                              No se pudo cargar el detalle.
                            </Typography>
                          )}
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Diálogo nuevo conteo */}
      <Dialog
        open={nuevoOpen}
        onClose={() => setNuevoOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2.5, border: `1px solid ${FP.color.border}` } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary, pb: 1 }}>
          Nuevo conteo
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.88rem", mb: 1.5 }}>
            Elige el alcance. Varias personas podrán unirse y escanear a la vez.
          </Typography>
          <RadioGroup value={alcance} onChange={(e) => setAlcance(e.target.value)}>
            <FormControlLabel
              value="T"
              control={<Radio size="small" />}
              label={
                <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: FP.color.textPrimary }}>
                  Toda la sucursal
                </Typography>
              }
            />
            <FormControlLabel
              value="M"
              control={<Radio size="small" />}
              label={
                <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: FP.color.textPrimary }}>
                  Una sola marca
                </Typography>
              }
            />
            <FormControlLabel
              value="D"
              control={<Radio size="small" />}
              label={
                <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: FP.color.textPrimary }}>
                  Un modelo de una marca
                </Typography>
              }
            />
          </RadioGroup>
          {alcance !== "T" ? (
            <TextField
              select
              fullWidth
              label="Marca"
              value={marca}
              onChange={(e) => {
                setMarca(e.target.value);
                setModelo("");
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ ...fpInputSx, mt: 1.5 }}
            >
              {marcas.length === 0 ? (
                <MenuItem value="" disabled>
                  Sin marcas con existencia
                </MenuItem>
              ) : (
                marcas.map((m) => (
                  <MenuItem key={m.marca} value={m.marca}>
                    {m.marca} ({m.unidades})
                  </MenuItem>
                ))
              )}
            </TextField>
          ) : null}
          {alcance === "D" ? (
            <TextField
              select
              fullWidth
              label="Modelo"
              value={modelo}
              onChange={(e) => setModelo(e.target.value)}
              disabled={!marca}
              InputLabelProps={{ shrink: true }}
              sx={{ ...fpInputSx, mt: 1.5 }}
            >
              {!marca ? (
                <MenuItem value="" disabled>
                  Selecciona primero la marca
                </MenuItem>
              ) : modelos.filter((m) => m.marca === marca).length === 0 ? (
                <MenuItem value="" disabled>
                  Sin modelos con existencia para esta marca
                </MenuItem>
              ) : (
                modelos
                  .filter((m) => m.marca === marca)
                  .map((m) => (
                    <MenuItem key={m.modelo} value={m.modelo}>
                      {m.modelo} ({m.unidades})
                    </MenuItem>
                  ))
              )}
            </TextField>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setNuevoOpen(false)}
            sx={{ textTransform: "none", fontWeight: 700, color: FP.color.textSecondary }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={abrirConteo}
            disabled={abriendo}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              bgcolor: FP.color.navy,
              boxShadow: "none",
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
            }}
          >
            {abriendo ? "Abriendo…" : "Abrir conteo"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de descarte */}
      <Dialog
        open={!!descartar}
        onClose={() => setDescartar(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2.5, border: `1px solid ${FP.color.border}` } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary, pb: 1 }}>
          Descartar conteo
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }}>
            Se descartará el conteo #{descartar?.id} ({descartar ? alcanceLabel(descartar) : ""}) con{" "}
            {descartar?.totalcontado || 0} unidades ya escaneadas. No aparecerá en el historial.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDescartar(null)}
            sx={{ textTransform: "none", fontWeight: 700, color: FP.color.textSecondary }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={descartarConteo}
            disabled={descartando}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              bgcolor: FP.color.danger,
              boxShadow: "none",
              "&:hover": { bgcolor: "#B91C1C", boxShadow: "none" },
            }}
          >
            {descartando ? "Descartando…" : "Descartar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
